import React, { useRef, useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import Form_Header from "../../../common/form-header/Form_Header";
import Common_Btn from "../../../common/common-btn/Common_Btn";
import White_Btn from "../../../common/common-btn/white-btn/White_Btn";
import "./Create_Step5.css";
import Preview_Modal from "../../../common/common-modal/preview-modal/Preview_Modal";
import { useRegistration } from "../../../../context/RegistrationContext";
import { submitStep5, getRegistrationProgress } from "../../../../utils/gymOwnerApi";

const Create_Step5 = ({ step, totalSteps, setStep }) => {
  const { userId, setRegistrationData } = useRegistration();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // -------- Multiple files --------
  const fileRefs = {
    aadhaarFront: useRef(null),
    aadhaarBack: useRef(null),
    voterId: useRef(null),
    passportDl: useRef(null),
  };

  const [files, setFiles] = useState({
    aadhaarFront: null,
    aadhaarBack: null,
    voterId: null,
    passportDl: null,
  });

  // -------- Single file (Passport photo) --------
  const singleFileRef = useRef(null);
  const [singleFile, setSingleFile] = useState(null);

  // -------- Validation errors --------
  const [fieldErrors, setFieldErrors] = useState({});

  // Load saved data on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (userId) {
        setLoadingData(true);
        try {
          const response = await getRegistrationProgress();
          if (response.success && response.data?.identity_proof) {
            const proof = response.data.identity_proof;
            const imgUrl = process.env.REACT_APP_IMG_URL || "";
            if (proof.aadhaar_front) {
              setFiles((prev) => ({
                ...prev,
                aadhaarFront: { preview: `${imgUrl}${proof.aadhaar_front}` },
              }));
            }
            if (proof.aadhaar_back) {
              setFiles((prev) => ({
                ...prev,
                aadhaarBack: { preview: `${imgUrl}${proof.aadhaar_back}` },
              }));
            }
            if (proof.voter_id) {
              setFiles((prev) => ({
                ...prev,
                voterId: { preview: `${imgUrl}${proof.voter_id}` },
              }));
            }
            if (proof.passport || proof.driving_license) {
              setFiles((prev) => ({
                ...prev,
                passportDl: {
                  preview: `${imgUrl}${proof.passport || proof.driving_license}`,
                },
              }));
            }
            if (proof.passport_size_photo) {
              setSingleFile({
                preview: `${imgUrl}${proof.passport_size_photo}`,
              });
            }
            setRegistrationData(response.data);
          }
        } catch (error) {
          console.error("Error loading progress:", error);
        } finally {
          setLoadingData(false);
        }
      }
    };
    loadProgress();
  }, [userId, setRegistrationData]);

  // -------- Aadhaar cross-validation --------
  // Returns an error object; empty object means valid.
  const validateAadhaar = (currentFiles) => {
    const hasFront = !!currentFiles.aadhaarFront;
    const hasBack = !!currentFiles.aadhaarBack;
    const errors = {};
    if (hasFront && !hasBack)
      errors.aadhaarBack = "Aadhaar Card (Back) is required when Front is uploaded";
    if (hasBack && !hasFront)
      errors.aadhaarFront = "Aadhaar Card (Front) is required when Back is uploaded";
    return errors;
  };

  const onSubmit = async () => {
    const newErrors = { ...validateAadhaar(files) };

    if (!singleFile) {
      newErrors.passportSizePhoto = "Passport Size Photo is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }

    if (!userId) {
      alert("Please complete previous steps first");
      setStep(1);
      return;
    }

    setLoading(true);
    try {
      const step5Data = {
        user_id: userId,
        aadhar_front: files.aadhaarFront?.file || null,
        aadhar_back: files.aadhaarBack?.file || null,
        voter_id: files.voterId?.file || null,
        passport: files.passportDl?.file || null,
        driving_license: files.passportDl?.file || null,
        passport_size_photo: singleFile?.file || null,
      };
      await submitStep5(step5Data);
      setStep(6);
    } catch (error) {
      console.error("Error submitting step 5:", error);
    } finally {
      setLoading(false);
    }
  };

  // -------- Modal --------
  const [modalShow, setModalShow] = useState(false);
  const [modalFile, setModalFile] = useState(null);

  // -------- Handlers --------
  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const newEntry = { file, name: file.name, preview: URL.createObjectURL(file) };
    const nextFiles = { ...files, [type]: newEntry };
    setFiles(nextFiles);

    // Re-validate Aadhaar pairing on every Aadhaar change, clear error if now valid
    if (type === "aadhaarFront" || type === "aadhaarBack") {
      const aadhaarErrors = validateAadhaar(nextFiles);
      setFieldErrors((prev) => ({
        ...prev,
        aadhaarFront: aadhaarErrors.aadhaarFront,
        aadhaarBack: aadhaarErrors.aadhaarBack,
      }));
    }
  };

  const handleSingleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSingleFile({ file, name: file.name, preview: URL.createObjectURL(file) });
    setFieldErrors((prev) => ({ ...prev, passportSizePhoto: undefined }));
  };

  const openPreviewModal = (file) => {
    setModalFile(file);
    setModalShow(true);
  };

  const handleFileButtonClick = (type) => {
    if (files[type]?.preview) {
      openPreviewModal(files[type]);
    } else {
      fileRefs[type].current.click();
    }
  };

  const handleSingleButtonClick = () => {
    if (singleFile?.preview) {
      openPreviewModal(singleFile);
    } else {
      singleFileRef.current.click();
    }
  };

  // Field config — label + key
  const idProofFields = [
    { label: "Aadhaar Card (Front)", key: "aadhaarFront" },
    { label: "Aadhaar Card (Back)", key: "aadhaarBack" },
    { label: "Voter ID Card", key: "voterId" },
    { label: "Passport / Driving License", key: "passportDl" },
  ];

  return (
    <section className="step3_wrapper step1_wrapper step4_wrapper step5_wrapper">
      <Form_Header
        title="Owner / Authorized Signatory KYC"
        subtitle="(Individual Proofs)"
        step={5}
        totalSteps={7}
      />

      <div className="container mt-4">
        {loadingData ? (
          <div className="text-center py-5">
            <p>Loading saved data...</p>
          </div>
        ) : (
          <>
            <div className="doc_header">
              <h5 className="doc_title">Proof of Identity</h5>
              <span className="doc_info">(Any 1 ID Proof of the following)</span>
            </div>

            {/* -------- ID Proofs -------- */}
            <div className="row mt-4">
              {idProofFields.map((item) => (
                <div className="col-lg-6 mb-4" key={item.key}>
                  <Form.Group>
                    <Form.Label>
                      {item.label}
                      {/* Show required star when the paired Aadhaar field has been filled */}
                      {item.key === "aadhaarBack" && files.aadhaarFront && (
                        <span className="text-danger"> *</span>
                      )}
                      {item.key === "aadhaarFront" && files.aadhaarBack && (
                        <span className="text-danger"> *</span>
                      )}
                    </Form.Label>

                    <div
                      className={`file_upload_wrapper ${fieldErrors[item.key] ? "border-danger" : ""
                        }`}
                    >
                      <span className="file_placeholder">
                        {files[item.key]?.name || "No File Uploaded"}
                      </span>

                      <div
                        className="upload_btn"
                        onClick={() => handleFileButtonClick(item.key)}
                      >
                        <Common_Btn
                          btnText={files[item.key] ? "Preview" : "Upload file"}
                        />
                      </div>

                      <input
                        ref={fileRefs[item.key]}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleFileChange(item.key, e)}
                      />
                    </div>

                    {fieldErrors[item.key] && (
                      <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                        {fieldErrors[item.key]}
                      </div>
                    )}
                  </Form.Group>
                </div>
              ))}
            </div>

            {/* -------- Passport Photo -------- */}
            <div className="row">
              <h4 className="passport_title mb-4 mt-4">Passport Size Photo*</h4>

              <div className="col-lg-6 mb-4">
                <Form.Group>
                  <Form.Label>
                    Passport Size Photo <span className="text-danger">*</span>
                  </Form.Label>

                  <div
                    className={`file_upload_wrapper ${fieldErrors.passportSizePhoto ? "border-danger" : ""
                      }`}
                  >
                    <span className="file_placeholder">
                      {singleFile?.name || "No File Uploaded"}
                    </span>

                    <div className="upload_btn" onClick={handleSingleButtonClick}>
                      <Common_Btn btnText={singleFile ? "Preview" : "Upload file"} />
                    </div>

                    <input
                      ref={singleFileRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleSingleFileChange}
                    />
                  </div>

                  {fieldErrors.passportSizePhoto && (
                    <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>
                      {fieldErrors.passportSizePhoto}
                    </div>
                  )}
                </Form.Group>
              </div>
            </div>

            {/* -------- Buttons -------- */}
            <div className="form_btn_holder">
              <White_Btn btnText="Back" onClick={() => setStep(4)} />
              <Common_Btn
                btnText={loading ? "Submitting..." : "Next"}
                onClick={onSubmit}
                disabled={loading}
              />
            </div>
          </>
        )}
      </div>

      <Preview_Modal
        show={modalShow}
        onHide={() => setModalShow(false)}
        file={modalFile}
        onUploadNew={() => {
          if (modalFile === singleFile) {
            singleFileRef.current.click();
          } else {
            const key = Object.keys(files).find((k) => files[k] === modalFile);
            fileRefs[key]?.current.click();
          }
        }}
        onDelete={() => {
          if (modalFile === singleFile) {
            setSingleFile(null);
            setFieldErrors((prev) => ({
              ...prev,
              passportSizePhoto: "Passport Size Photo is required",
            }));
          } else {
            const key = Object.keys(files).find((k) => files[k] === modalFile);
            const nextFiles = { ...files, [key]: null };
            setFiles(nextFiles);
            // Re-validate after deletion
            if (key === "aadhaarFront" || key === "aadhaarBack") {
              const aadhaarErrors = validateAadhaar(nextFiles);
              setFieldErrors((prev) => ({
                ...prev,
                aadhaarFront: aadhaarErrors.aadhaarFront,
                aadhaarBack: aadhaarErrors.aadhaarBack,
              }));
            }
          }
          setModalShow(false);
        }}
      />
    </section>
  );
};

export default Create_Step5;